import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import LeporelloDay from './LeporelloDay';
import fetchLeporelloApi from '../../helpers/fetchLeporelloApi';
import usePrevious from '../../hooks/usePrevious';
import useTranslation from '../../hooks/useTranslation';

const checkDayIsEqual = (date, compareDate) => {
  if (compareDate instanceof Date) {
    const compareDateString =
      compareDate.toLocaleDateString('de-DE', { year: 'numeric' }) +
      '-' +
      compareDate.toLocaleDateString('de-DE', { month: '2-digit' }) +
      '-' +
      compareDate.toLocaleDateString('de-DE', { day: '2-digit' });

    return compareDateString === date;
  }
  return false;
};

const checkIsOutdatedDay = date => {
  const currentDate = new Date();
  const currentDateString =
    currentDate.toLocaleDateString('de-DE', { year: 'numeric' }) +
    '-' +
    currentDate.toLocaleDateString('de-DE', { month: '2-digit' }) +
    '-' +
    currentDate.toLocaleDateString('de-DE', { day: '2-digit' });

  return currentDateString > date;
};

const dateToString = date =>
  date ? date.getDate() + '-' + (date.getMonth() + 1) + '-' + date.getFullYear() : null;

const deduplicateDays = days => {
  for (let i = 0; i < days.length - 1; i++) {
    if (days[i].date === days[i + 1].date) {
      days[i].items.push(...days[i + 1].items); // Append items of the duplicate day
      days.splice(i + 1, 1); // Cut out former duplicate day
    }
  }
  return days;
};

const LeporelloResults = props => {
  const _ = useTranslation();
  const requestControllerRef = useRef(null);
  const daysRef = useRef([]);

  // Basic Configuration
  const {
    customPaginationButton,
    hideInfoButtons,
    hideTicketButtons,
    mode,
    numberOfInitialResults,
    searchQuery,
    type,
    scrollToDate,
    periodReport,
    periodStartDate,
    periodEndDate,
    includedProductions,
    singleEventIncludedSingleEvents,
    singleEventExcludedSingleEvents,
    onAllHasLoaded
  } = props;
  const startDate = props.startDate || new Date();
  const endDate = props.endDate || null;

  const previousSearchQuery = usePrevious(searchQuery);
  const previousIncludedProductions = usePrevious(includedProductions);
  const previousStartDate = usePrevious(dateToString(startDate));
  const previousEndDate = usePrevious(dateToString(endDate));

  // Advanced Filters
  const advancedFilters = {
    singleEventIncludedSingleEvents: props.singleEventIncludedSingleEvents,
    singleEventExcludedSingleEvents: props.singleEventExcludedSingleEvents,
    singleEventIncludeTags: props.singleEventIncludeTags,
    singleEventExcludeTags: props.singleEventExcludeTags,
    performanceIncludeProductions: props.performanceIncludeProductions,
    performanceExcludeProductions: props.performanceExcludeProductions,
    includeLocations: props.includeLocations,
    excludeLocations: props.excludeLocations,
    performancesInclude: props.performancesInclude
  };

  // State values
  const [initialResults, setInitialResults] = useState([]);
  const [appendedResults, setAppendedResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canLoadAll, setCanLoadAll] = useState(false);
  const [hasLoadedAll, setHasLoadedAll] = useState(false);

  // Handle loading data (initially)
  const doLoadInitial = async () => {
    // Cancel previous request
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }

    // Register current request
    const controller = new AbortController();
    requestControllerRef.current = controller;

    // Reset UI status
    setInitialResults([]);
    setAppendedResults([]);
    setLoading(true);
    setCanLoadAll(false);
    setHasLoadedAll(false);

    const limit = mode === 'ITEMS' ? numberOfInitialResults : -1;
    const offset = 0;

    // Set End Date depending on numberOfInitialResults
    let displayedEndDate = new Date(startDate.getTime());
    displayedEndDate.setDate(displayedEndDate.getDate() + numberOfInitialResults - 1);

    // If the calculated end date is later than the actual end date, cap it
    if (endDate && displayedEndDate > endDate) {
      displayedEndDate = endDate;
    }

    // Fetch Leporello data
    const [results, apiCanLoadAll] = await fetchLeporelloApi(
      requestControllerRef,
      mode,
      type,
      searchQuery,
      startDate,
      mode === 'DAYS' && numberOfInitialResults > -1 ? displayedEndDate : endDate,
      limit,
      offset,
      advancedFilters,
      includedProductions,
      singleEventIncludedSingleEvents,
      singleEventExcludedSingleEvents
    );

    if (results) {
      // Set results and change loading state
      setInitialResults(results);
      setLoading(false);
      requestControllerRef.current = null;
    }

    // Show "load all" button when API reports more items to load
    // Show "load all" button when displayed end date is not the desired end date yet
    setCanLoadAll(
      mode === 'ITEMS' ? apiCanLoadAll : displayedEndDate < endDate && numberOfInitialResults > -1
    );
  };

  // Handle loading data (all)
  const doLoadAll = async () => {
    // Cancel previous request
    if (requestControllerRef.current) {
      requestControllerRef.current.abort();
    }

    // Register current request
    const controller = new AbortController();
    requestControllerRef.current = controller;

    // Update UI status
    setAppendedResults([]);
    setLoading(true);
    setCanLoadAll(true);
    setHasLoadedAll(false);

    const limit = -1;
    const offset = mode === 'ITEMS' ? numberOfInitialResults : 0;

    // Set End Date depending on mode
    const displayedStartDate = new Date(startDate.getTime());
    if (mode === 'DAYS' && numberOfInitialResults > -1) {
      displayedStartDate.setDate(displayedStartDate.getDate() + numberOfInitialResults);
    }

    // Fetch Leporello data
    const [results] = await fetchLeporelloApi(
      requestControllerRef,
      mode,
      type,
      searchQuery,
      displayedStartDate,
      endDate,
      limit,
      offset,
      advancedFilters,
      includedProductions,
      singleEventIncludedSingleEvents,
      singleEventExcludedSingleEvents
    );

    // Set results and change loading state
    if (results) {
      // Set results and change loading state
      setAppendedResults(results);
      setLoading(false);
      setHasLoadedAll(true);
      onAllHasLoaded();
      requestControllerRef.current = null;
    }
  };

  // Check if day is in given period
  const checkIsDayInPeriod = day => {
    if (periodReport && periodStartDate && periodEndDate) {
      // if day is between period return period report
      if (
        Date.parse(day) >= Date.parse(periodStartDate) &&
        Date.parse(day) <= Date.parse(periodEndDate)
      ) {
        return periodReport;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  // Trigger initial load when parameters change
  useEffect(() => {
    if (
      previousSearchQuery !== searchQuery ||
      previousStartDate !== dateToString(startDate) ||
      previousEndDate !== dateToString(endDate) ||
      previousIncludedProductions !== includedProductions
    ) {
      doLoadInitial();
    }
  }, [searchQuery, startDate, endDate, includedProductions]);

  /**
   * Handle keyboard navigation for results
   */
  const handleDayKeyDown = (e, index) => {
    if (!daysRef.current[index]) return; // If day is not available, do nothing
    let performances = null;

    // Check if current day has performances
    if (daysRef.current[index].dataset && daysRef.current[index].dataset.empty === 'false') {
      performances = daysRef.current[index].childNodes[1]?.childNodes;
    }

    if (
      (e.key === 'Tab' || e.key === 'ArrowDown' || e.key === 'ArrowUp') &&
      daysRef.current[index] === e.target
    ) {
      if ((e.key === 'Tab' && e.shiftKey) || e.key === 'ArrowUp') {
        // Only override tab behavior if the current day is NOT focused
        if (index > 0) {
          e.preventDefault(); // Prevent default tab behavior

          // If Shift + Tab is pressed, focus previous day
          daysRef.current[index - 1].focus();
        }
      } else if (index + 1 < daysRef.current.length && (e.key === 'Tab' || e.key === 'ArrowDown')) {
        e.preventDefault(); // Prevent default tab behavior

        // Focus next day
        daysRef.current[index + 1].focus();
      }
    } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && performances) {
      // Handle arrow key navigation within performances
      e.preventDefault(); // Prevent default arrow key behavior

      // Get focused performance index
      const focusedElementIndex = e.target ? Array.from(performances).indexOf(e.target) : -1;

      // Navigate between performances within the same day
      if (e.key === 'ArrowUp' && focusedElementIndex > 0) {
        performances[focusedElementIndex - 1].focus();
      } else if (e.key === 'ArrowDown' && focusedElementIndex < performances.length - 1) {
        performances[focusedElementIndex + 1].focus();
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      if (performances && performances.length > 0 && e.target === daysRef.current[index]) {
        e.preventDefault();
        performances[0].focus();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();

      // If Escape is pressed, focus the first day
      if (daysRef.current[index]) {
        daysRef.current[index].focus();
      }
    }
  };

  // Merge results into one unified array
  const mergedResults = deduplicateDays([...initialResults, ...appendedResults]);

  return (
    <div className='c-leporello-results' role='region' aria-label={_('leporello_results')}>
      <div className='c-leporello-results__days'>
        {mergedResults.map((day, index) => (
          <LeporelloDay
            key={`${day.date}-${day.items.length}`}
            index={index}
            day={day}
            isCurrentDay={checkDayIsEqual(day.date, new Date())}
            isOutdated={checkIsOutdatedDay(day.date)}
            scrollToDay={checkDayIsEqual(day.date, scrollToDate)}
            {...{ hideInfoButtons, hideTicketButtons }}
            periodReport={checkIsDayInPeriod(day.date)}
            onKeyDown={e => {
              handleDayKeyDown(e, index);
            }}
            ref={el => (daysRef.current[index] = el)}
          />
        ))}
        {loading ? (
          <>
            <LeporelloDay skeleton={true} day={{}} index={0} />
            <LeporelloDay skeleton={true} day={{}} index={1} />
            <LeporelloDay skeleton={true} day={{}} index={2} />
          </>
        ) : null}
        {mergedResults.length === 0 && !loading ? (
          <div className='c-leporello-results__no-performances-found'>
            <h3>{_('leporello_no_performances_found')}</h3>
          </div>
        ) : null}
      </div>
      {canLoadAll && !hasLoadedAll && !loading && !customPaginationButton ? (
        <div className='c-leporello-results__navigation'>
          <button
            type='button'
            className='c-button'
            onClick={doLoadAll}
            aria-label={_('leporello_show_all_dates')}
          >
            {_('leporello_show_all_dates')}
          </button>
        </div>
      ) : null}
      {customPaginationButton ? (
        <div className='c-leporello-results__navigation'>
          <a
            href={customPaginationButton.url}
            target={customPaginationButton.target}
            className='c-button'
            aria-label={customPaginationButton.title}
          >
            {customPaginationButton.title}
          </a>
        </div>
      ) : null}
    </div>
  );
};

LeporelloResults.propTypes = {
  mode: PropTypes.oneOf(['DAYS', 'ITEMS']).isRequired,
  type: PropTypes.string,
  hideInfoButtons: PropTypes.bool,
  hideTicketButtons: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  searchQuery: PropTypes.string,
  numberOfInitialResults: PropTypes.number,
  singleEventIncludedSingleEvents: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  singleEventExcludedSingleEvents: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  singleEventIncludeTags: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  singleEventExcludeTags: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  performanceIncludeProductions: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  performanceExcludeProductions: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  includeLocations: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  excludeLocations: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  performancesInclude: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  customPaginationButton: PropTypes.object,
  scrollToDate: PropTypes.instanceOf(Date),
  triggerEvent: PropTypes.object,
  periodStartDate: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  periodEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  periodReport: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  includedProductions: PropTypes.array,
  onAllHasLoaded: PropTypes.func
};

export default LeporelloResults;
